import _ from 'lodash'
import { blobToDataURL } from 'blob-util'
import JSZip from 'jszip'
import { jsPDF } from 'jspdf'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { saveAs } from 'file-saver'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { useRequestHandler } from '@/hooks/useRequestHandler'

import Documents, { DocumentsHandle, DocumentsProps } from '../components/Documents'
import { GenerateFSPFormValues } from '../components/GenerateFSPDialog'
import { GenerateReportFormValues } from '../components/GenerateReportDialog'
import GeneratePrescriptionDialog from '../components/GeneratePrescriptionDialog'
import GenerateDesmosActDialog, { GenerateDesmosActDto, GenerateDesmosActFormValues } from '../components/GenerateDesmosActDialog'

import { ObjectType } from '@/core/models/document.model'
import { useGetConsultationQuery } from '@/core/services/consultation'
import { useGetLicensesQuery } from '@/core/services/license'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { getMagicLinkSuccess, selectAuthUser, selectMagicLink } from '@/redux/reducers/auth.reducer'
import {
  generateFSP,
  generateReport,
  selectConsultationCreateRequestTracker,
} from '@/redux/reducers/consultation.reducer'
import {
  addDocument,
  addPrescription,
  deleteDocuments,
  selectAddPrescriptionRequestTracker,
  selectDocumentCreateRequestTracker,
  selectDocumentDeleteRequestTracker,
  selectDocumentUpdateRequestTracker,
  selectSendDocumentsByEmailRequestTracker,
  sendDocumentsByEmail,
  updateFilename,
} from '@/redux/reducers/document.reducer'
import { getOrCreateOrdoclicPatient, selectGetOrCreateOrdoclicPatientRequestTracker, selectPatientById } from '@/redux/reducers/patient.reducer'

import { isStaff } from '@/utils/type-guards'
import { ExternalService } from '@/constants/external-services'
import { getExternalId } from '@/core/models/staff.model'
import { audioExtensions, getFileExtension, getFilename, imgExtensions, pdfExtensions } from '@/utils/document'
import { useCreateDesmosActMutation } from '@/core/services/desmos'
import { useLazyGetOrCreateDesmosPatientQuery } from '@/core/services/patient'
import { useCreateUploadGrantMutation } from '@/core/services/file'
import { useOcrAndAddToConsultationMutation } from '@/core/services/medconnect-agent-service'
import { format } from 'date-fns'

import { skipToken } from '@reduxjs/toolkit/query'
import { Consultation } from '@/core/models/consultation.model'
import { FormSubmission } from '@/core/models/form.model'
import { useLanguage } from '@/hooks/useLanguage'
import { useIsUS } from '@/hooks/useIsUS'
import { parseMeasurementValue, toMeasurementUnit } from '@/utils/unit-system'
import { useMeasurementUnitSystem } from '@/hooks/useMeasurementUnitSystem'
import { getApiErrorMessage } from '@/utils/errors'

type DocumentsContainerProps = {
  consultationId?: string
  isLocal?: boolean
  readOnly?: boolean
}

export function DocumentsContainer(props: DocumentsContainerProps) {
  const { consultationId, isLocal = false, readOnly = false } = props
  const appDispatch = useAppDispatch()
  const { t, i18n } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const isUS = useIsUS()
  const { unitSystem } = useMeasurementUnitSystem()

  const documentsRef = useRef<DocumentsHandle>(null)

  const [generatePrescriptionDialogOpen, setGeneratePrescriptionDialogOpen] = useState(false)
  const [generateDesmosActDialogOpen, setGenerateDesmosActDialogOpen] = useState(false)
  const [isExporting, setIsExporting] = useState<boolean>(false)

  const [createUploadGrant] = useCreateUploadGrantMutation()

  // Desmos integration
  const [getOrCreateDesmosPatient, { data: desmosPatientData, isLoading: isDesmosPatientLoading }] = useLazyGetOrCreateDesmosPatientQuery()
  const [createDesmosAct, { isLoading: isCreatingDesmosAct }] = useCreateDesmosActMutation()

  const [ocrAndAddToConsultation, { isLoading: isProcessingOcr, originalArgs }] = useOcrAndAddToConsultationMutation()

  const { dispatch: createRequestDispatch } = useRequestHandler({
    onSuccess: () => enqueueSnackbar(t('notification.document.create.success'), { variant: 'success' }),
    onError: () => enqueueSnackbar(t('notification.document.create.error'), { variant: 'error' }),
    requestTrackerSelector: selectDocumentCreateRequestTracker,
  })

  const { dispatch: deleteRequestDispatch, requestTracker: documentDeleteRequestTracker } = useRequestHandler({
    onSuccess: () => enqueueSnackbar(t('notification.document.delete.success'), { variant: 'success' }),
    onError: () => enqueueSnackbar(t('notification.document.delete.error'), { variant: 'error' }),
    requestTrackerSelector: selectDocumentDeleteRequestTracker,
  })

  const generateReportRequestHandler = useRequestHandler({
    onSuccess: () => enqueueSnackbar(t('notification.document.create.success'), { variant: 'success' }),
    onError: () => enqueueSnackbar(t('notification.document.create.error'), { variant: 'error' }),
    requestTrackerSelector: selectConsultationCreateRequestTracker,
  })

  const generateFspRequestHandler = useRequestHandler({
    onSuccess: () => enqueueSnackbar(t('notification.document.create.success'), { variant: 'success' }),
    onError: () => enqueueSnackbar(t('notification.document.create.error'), { variant: 'error' }),
    requestTrackerSelector: selectConsultationCreateRequestTracker,
  })

  const getOrCreateOrdoclicPatientRequestHandler = useRequestHandler({
    onSuccess: () => {
      // if success then state of patient should be updated...
      const ordoclicId = getExternalId(patient?.externalAccounts, 'ordoclic', authUser?.establishment?._id)
      if (ordoclicId) {
        const url = `/prescription?consultationId=${consultationId}&ordoclicPatientId=${ordoclicId}${
          authUser?.establishment?.ordoclicEntityId ? `&entityId=${authUser.establishment.ordoclicEntityId}` : ''
        }`
        window.open(url, '_blank')
      } else {
        enqueueSnackbar(t('notification.ordoclic.patient.create.error'), { variant: 'error' })
      }
    },
    onError: () => {
      enqueueSnackbar(t('notification.ordoclic.patient.create.error'), { variant: 'error' })
    },
    requestTrackerSelector: selectGetOrCreateOrdoclicPatientRequestTracker,
  })

  const documentUpdateRequestHandler = useRequestHandler({
    onSuccess: () => enqueueSnackbar(t('notification.document.updated.success'), { variant: 'success' }),
    onError: () => enqueueSnackbar(t('notification.document.updated.error'), { variant: 'error' }),
    requestTrackerSelector: selectDocumentUpdateRequestTracker,
  })

  const sendDocumentsByEmailRequestHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.document.sendDocumentsByEmail.success'), { variant: 'success' })
      documentsRef.current?.toggleEmailDocumentsDialog()
    },
    onError: () => {
      enqueueSnackbar(t('notification.document.sendDocumentsByEmail.error'), { variant: 'error' })
    },
    requestTrackerSelector: selectSendDocumentsByEmailRequestTracker,
  })

  const authUser = useAppSelector(selectAuthUser)
  const { data: consultation, refetch: refetchConsultation } = useGetConsultationQuery(consultationId ?? skipToken)
  const patient = useAppSelector((state) => (consultation ? selectPatientById(state, consultation.patient._id) : undefined))
  const qrCode = useAppSelector(selectMagicLink)

  const { data: licenses } = useGetLicensesQuery({ id: authUser?.licenses }, { skip: !authUser?.licenses.length })

  const documents = useMemo(() => {
    if (!consultation) return []

    return _.map(consultation.files, (file) => ({
      key: file.key,
      name: getFilename(file.filename),
      type: getFileExtension(file.filename),
      src: file.url,
    }))
  }, [consultation])

  const emails = useMemo(() => {
    if (!consultation) return []

    return _.filter([consultation.patient.email, consultation.medecin?.email, consultation.pds?.email], (email) => !_.isEmpty(email)) as string[]
  }, [consultation])

  const conclusionSentences = useMemo(() => {
    if (!isStaff(authUser)) return []
    return _.filter(authUser.conclusionSentences || [], (sentence) => sentence !== '')
  }, [authUser])

  const dictaphoneSentences = useMemo(() => {
    return _.map(consultation?.dictaphones, (dictaphone) => dictaphone.text || '')
  }, [consultation])

  const initialReportValues: GenerateReportFormValues = useMemo(
    () => ({
      establishment: {
        name: _.get(authUser?.establishment, 'name', ''),
        address:
          _.get(authUser?.establishment, 'address', '') +
          ' ' +
          _.get(authUser?.establishment, 'zipCode', '') +
          ' ' +
          _.get(authUser?.establishment, 'city', '') +
          ' ' +
          _.get(authUser?.establishment, 'country', ''),
        phone: _.get(authUser?.establishment, 'phone', ''),
      },
      logo: authUser?.establishment?.logo?.url || import.meta.env.VITE_LOGO_URL || '',
      title: t('page.liveConsultation.generateReportDialog.pdf.title'),
      startDate: consultation?.startDate || '',
      pds: consultation?.pds ? _.trim(`${consultation.pds.userInfo?.name || ''} ${consultation.pds.userInfo?.surname || ''}`) : '',
      patient: {
        firstName: consultation?.patient?.userInfo?.name || '',
        lastName: consultation?.patient?.userInfo?.surname || '',
        gender: consultation?.patient?.userInfo?.gender || '',
        id: consultation?.patient?.id || '',
        birthday: consultation?.patient?.userInfo?.birthday ? dayjs(consultation.patient.userInfo.birthday).format(isUS ? 'MM/DD/YYYY' : 'DD/MM/YYYY') : '',
        secu: consultation?.patient?.secu || '',
        attendingPhysician: consultation?.patient?.attendingPhysician
          ? `${consultation.patient.attendingPhysician.firstname} ${consultation.patient.attendingPhysician.lastname}`
          : '',
        insured: consultation?.patient?.insured || '',
        insuredRelationship: consultation?.patient?.insuredRelationship || '',
        insuranceProvider: consultation?.patient?.insuranceProvider || '',
        primaryInsurance: consultation?.patient?.primaryInsurance || '',
        secondaryInsurance: consultation?.patient?.secondaryInsurance || '',
        primaryPolicy: consultation?.patient?.primaryPolicy || '',
      },
      mesures: !consultation
        ? []
        : _.map(_.sortBy(consultation.mesures, 'order'), (mesure) => ({
            ...mesure,
            unit: toMeasurementUnit(mesure.unit, unitSystem),
            value: parseMeasurementValue(mesure.unit, mesure.value, unitSystem),
          })),
      mesuresNote: consultation?.commentaire || '',
      images: _.map(
        _.filter(consultation?.files, (file) => imgExtensions.some((ext) => file.filename.toLocaleLowerCase().endsWith(ext))),
        (file) => ({ ...file, checked: false })
      ),
      audios: _.map(
        _.filter(consultation?.files, (file) => audioExtensions.some((ext) => file.filename.toLocaleLowerCase().endsWith(ext))),
        (file) => ({ ...file, checked: false })
      ),
      pdfs: _.map(
        _.filter(consultation?.files, (file) => pdfExtensions.some((ext) => file.filename.toLocaleLowerCase().endsWith(ext))),
        (file) => ({ ...file, checked: false })
      ),
      stethoscopes: _.map(
        _.filter(consultation?.files, (file) => audioExtensions.some((ext) => file.filename.toLocaleLowerCase().endsWith(ext))),
        (file) => ({ ...file, checked: false })
      ),
      documents: _.map(consultation?.files, (file) => ({ ...file, checked: false })),
      biologicals:
        consultation?.biologicals.map(({ files, label, comment, value, unit }) => ({
          label,
          comment,
          value,
          unit,
          files: consultation.files.filter((file) => files.includes(file.key)).map((file) => ({ ...file, checked: false })),
        })) ?? [],
      eSignature: isStaff(authUser) && authUser.signature ? 'data:image/png;base64,' + authUser.signature.data : '',
      manualSignature: authUser ? `${authUser.userInfo.name} ${authUser.userInfo.surname}` : '',
      signatureMode: isStaff(authUser) && authUser.signature ? 'e-signature' : ('manual' as GenerateReportFormValues['signatureMode']),
      formSubmissions: getFormSubmissionFields(getLastFormSubmission(consultation)),
    }),
    [authUser, consultation, t, unitSystem]
  )

  const formSubmissionOptions = useMemo(() => buildFormSubmissionOptions(consultation), [consultation])

  const initialFSPValues = useMemo(
    () => ({
      Acts: [
        {
          code: 'TCG',
          price: 25,
          date: new Date().toISOString(),
        },
      ],
    }),
    []
  )

  const initialDesmosActValues: GenerateDesmosActFormValues = useMemo(
    () => ({
      libelle: 'Consultation de télémédecine',
      supplement: '0',
      nom_praticien: authUser?.userInfo.surname,
      prenom_praticien: authUser?.userInfo.name,
      rpps_praticien: isStaff(authUser) ? authUser?.cps : '',
    }),
    [authUser]
  )

  const genereateQR = useCallback(() => {
    if (!consultationId) return
    createUploadGrant({ objectType: 'consultation', objectId: consultationId })
      .unwrap()
      .then(({ url }) => appDispatch(getMagicLinkSuccess(url)))
      .catch(() => undefined)
  }, [consultationId, appDispatch, createUploadGrant])

  // cal it once at the beginning
  useEffect((): void => {
    genereateQR()
  }, [genereateQR])

  const addPrescriptionRequestHandler = useRequestHandler({
    onSuccess: () => enqueueSnackbar(t('notification.document.create.success'), { variant: 'success' }),
    onError: () => enqueueSnackbar(t('notification.document.create.error'), { variant: 'error' }),
    requestTrackerSelector: selectAddPrescriptionRequestTracker,
  })

  const handleAddPrescription = useCallback(
    (prescriptionId: string) => {
      if (consultationId) {
        addPrescriptionRequestHandler.dispatch(addPrescription({ consultationId, prescriptionId }))
      }
    },
    [consultationId, addPrescriptionRequestHandler]
  )

  const handleDelete = (deletedDocuments: DocumentsProps['documents']) => {
    if (!consultationId || !deletedDocuments.length) return

    const documents = _.map(deletedDocuments, (document) => ({
      filename: `${document.name}.${document.type}`,
      key: document.key,
      objectType: 'consultation' as ObjectType,
    }))
    deleteRequestDispatch(deleteDocuments(documents, 'consultation', consultationId))
  }

  const handleExport = async (exportedDocuments: DocumentsProps['documents']) => {
    setIsExporting(true)
    const zip = new JSZip()
    let patientFolderZip = zip.folder(_.trim(`${consultation?.patient.userInfo.surname || ''}-${consultation?.patient.userInfo.name || ''}`, '-'))
    try {
      await Promise.all(
        _.map(exportedDocuments, async (document) => {
          const response = await fetch(document.src, { method: 'GET' })
          const blob = await response.blob()
          patientFolderZip?.file(`${document.name}.${document.type}`, blob, { binary: true })
        })
      )
      const generatedContent = await zip.generateAsync({ type: 'blob' })
      saveAs(generatedContent, 'consultation-files.zip')
    } catch (err) {
      console.log(err)
      enqueueSnackbar(t('notification.document.export.error', { variant: 'error' }))
    } finally {
      setIsExporting(false)
    }
  }

  const handleGenerateFSP = async (values: GenerateFSPFormValues) => {
    if (!consultationId) return
    generateFspRequestHandler.dispatch(generateFSP(consultationId, values.Acts))
  }

  const handleGenerateReport = async (values: GenerateReportFormValues) => {
    const reportValues: Record<string, any> = {
      ..._.omit(values, ['eSignature', 'manualSignature', 'signatureMode', 'audios', 'pdfs']),
      images: _.filter(values.images, (doc) => doc.checked).map((doc) => _.omit(doc, ['checked', 'objectType', 'url'])),
      documents: _.filter([...values.pdfs, ...values.audios], (doc) => doc.checked).map((doc) => _.omit(doc, ['checked', 'objectType', 'url'])),
      biologicals: _.map(values.biologicals, (doc) => ({ ...doc, files: doc.files.filter((file) => file.checked).map((file) => file.key) })),
      stethoscopes: _.compact(_.filter(values.stethoscopes, (stethoscope) => stethoscope.checked).map((stethoscope) => stethoscope.waveform)),
    }
    if (!values.logo.startsWith('data')) {
      reportValues.logo = {
        // if values.logo is relative path than add window.origin else use as it is
        url: values.logo.startsWith('/') ? window.origin + values.logo : values.logo,
        // url: window.origin + values.logo,
      }
    } else {
      reportValues.logo = {
        dataUri: values.logo,
        format: values.logo.substring(values.logo.indexOf('/') + 1, values.logo.indexOf(';')),
      }
    }
    switch (values.signatureMode) {
      case 'e-signature':
        reportValues.signature = values.eSignature
        break
      case 'manual':
        reportValues.signature = values.manualSignature
        break
      default:
        reportValues.signature = ''
        break
    }
    if (consultationId) generateReportRequestHandler.dispatch(generateReport(consultationId, reportValues))
  }

  const handleGeneratePrescriptionButtonClick = () => {
    if (!consultation?.patient?._id) return

    // Always call API to ensure migration and validation
    getOrCreateOrdoclicPatientRequestHandler.dispatch(getOrCreateOrdoclicPatient(consultation.patient._id))
  }

  const handleGenerateDesmosActButtonClick = () => {
    if (!patient) return
    setGenerateDesmosActDialogOpen(true)
  }

  const handleCreateDesmosAct = async (actValues: GenerateDesmosActDto) => {
    if (!patient) return

    try {
      // First, get or create Desmos patient
      const patientResult = await getOrCreateDesmosPatient(patient._id).unwrap()

      // Then create the act
      const result = await createDesmosAct({
        patientId: patient._id,
        actData: {
          libelle: actValues.libelle,
          date_realisation: format(new Date(), 'dd/MM/yyyy HH:mm:ss'),
          honoraire: actValues.honoraire,
          nom_praticien: actValues.nom_praticien,
          prenom_praticien: actValues.prenom_praticien,
          rpps_praticien: actValues.rpps_praticien,
          id_transposition: actValues.id_transposition,
          supplement: actValues.supplement,
        },
      }).unwrap()

      enqueueSnackbar(t('notification.desmos.act.create.success'), { variant: 'success' })
      setGenerateDesmosActDialogOpen(false)
    } catch (error) {
      console.error('Error creating Desmos act:', error)
      enqueueSnackbar(t('notification.desmos.act.create.error'), { variant: 'error' })
    }
  }

  const handlePrintImages = async (printedDocuments: DocumentsProps['documents']) => {
    try {
      if (!consultation) return
      const doc = new jsPDF('p', 'mm', 'a4')
      doc.setFontSize(10)
      // The heading was hardcoded French ("Consultation du <date> à <time>") with
      // French date/time formats, so every locale printed a French title. Both the
      // wording and the formats now follow the active locale.
      const startDate = dayjs(consultation.startDate).locale(i18n.language)
      doc.text(
        t('page.liveConsultation.documents.printImagesTitle', {
          date: startDate.format('LL'),
          time: startDate.format('LT'),
        }),
        10,
        20
      )
      doc.line(10, 25, 170, 25)
      let px = 10
      let py = 30
      const imageDataURLs = await Promise.all(
        _.map(printedDocuments, async (document) => {
          const response = await fetch(document.src, { method: 'GET' })
          const blob = await response.blob()
          return await blobToDataURL(blob)
        })
      )
      imageDataURLs.forEach((dataURL, index) => {
        doc.addImage(dataURL, 'png', px, py, 90, 90)
        if ((index + 1) % 2 === 0) {
          px = 10
          py = py + 95
        } else {
          px = px + 95
        }
        if ((index + 1) % 4 === 0) {
          doc.addPage()
          px = 15
          py = 35
        }
      })

      const blob = doc.output('blob')
      const blobUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))

      const iframe = document.createElement('iframe')
      iframe.src = blobUrl
      iframe.style.display = 'none'
      document.body.appendChild(iframe)
      iframe.contentWindow?.print()
    } catch (err) {
      console.log(err)
      enqueueSnackbar(t('notification.document.printImage.error', { variant: 'error' }))
    }
  }

  const handleRefreshDocument = async () => {
    if (!consultationId) return
    try {
      await refetchConsultation()
      enqueueSnackbar(t('notification.documents.updated.success'), { variant: 'success' })
    } catch {
      enqueueSnackbar(t('notification.documents.updated.error'), { variant: 'error' })
    }
  }

  const handleSaveFileAnnotation = (file: File, key: string) => {
    if (consultationId) createRequestDispatch(addDocument({ documentFile: file, objectType: 'consultation', objectId: consultationId }))
  }

  const handleSaveFileName = (filename: string, key: string) => {
    const file = _.find(consultation?.files, (file) => file.key === key)
    if (!file || !consultationId) return
    documentUpdateRequestHandler.dispatch(updateFilename(_.omit(file, 'url'), filename, 'consultation', consultationId))
  }

  const handleSendDocumentsByEmail = (emails: string[], documents: DocumentsProps['documents'], pdfPassword?: string) => {
    if (!consultationId) return
    sendDocumentsByEmailRequestHandler.dispatch(
      sendDocumentsByEmail({
        objectType: 'consultation',
        emails,
        objectId: consultationId,
        filenames: documents.map((doc) => `${doc.name}.${doc.type}`),
        ...(pdfPassword ? { pdfPassword } : {}),
      })
    )
  }

  const handleUpload = (file: File) => {
    if (consultationId) createRequestDispatch(addDocument({ documentFile: file, objectType: 'consultation', objectId: consultationId }))
  }

  const handleOcrDocument = async (fileKey: string) => {
    if (!consultationId) return

    try {
      const result = await ocrAndAddToConsultation({
        fileKey,
        consultationId,
      }).unwrap()

      const hasBiologicalTests =
        (result.biologicalTestsAdded && result.biologicalTestsAdded > 0) || (result.biologicalTestsUpdated && result.biologicalTestsUpdated > 0)
      const hasVitalSigns = result.vitalSignsUpdated && result.vitalSignsUpdated > 0
      const hasAnyData = hasBiologicalTests || hasVitalSigns

      if (result.success && hasAnyData) {
        const messages: string[] = []
        if (hasBiologicalTests) {
          const biologicalCount = (result.biologicalTestsAdded || 0) + (result.biologicalTestsUpdated || 0)
          messages.push(
            t('notification.document.ocr.biologicalTests.success', {
              count: biologicalCount,
              defaultValue: `Successfully processed ${biologicalCount} biological test(s)`,
            })
          )
        }
        if (hasVitalSigns) {
          messages.push(
            t('notification.document.ocr.vitalSigns.success', {
              count: result.vitalSignsUpdated,
              defaultValue: `Successfully updated ${result.vitalSignsUpdated} vital sign(s)`,
            })
          )
        }
        enqueueSnackbar(messages.join('\n'), {
          variant: 'success',
        })
      } else if (result.success && !hasAnyData) {
        enqueueSnackbar(t('notification.document.ocr.noData'), { variant: 'warning' })
      } else {
        // Operation failed
        enqueueSnackbar(t('notification.document.ocr.error'), { variant: 'error' })
      }
    } catch (e: unknown) {
      enqueueSnackbar(getApiErrorMessage(e, t('notification.document.ocr.error')), { variant: 'error' })
    }
  }

  if (!consultation) return null

  return (
    <>
      <Documents
        dictaphoneSentences={dictaphoneSentences}
        conclusionsSentences={conclusionSentences}
        ref={documentsRef}
        documents={documents}
        disableEPrescription={
          isStaff(authUser) &&
          !(authUser?.externalAccounts?.ordoclic && _.uniq(_.flatten(licenses?.map((license) => license.services))).includes(ExternalService.ordoclic))
        }
        disableDesmosAct={!isStaff(authUser) || !authUser?.establishment?.desmosConfig}
        emails={emails}
        isDeleting={documentDeleteRequestTracker.loading}
        isDoctor={isStaff(authUser) && authUser?.status === 'MEDECIN'}
        isExporting={isExporting}
        isSendingByEmail={sendDocumentsByEmailRequestHandler.requestTracker.loading}
        isGeneratingReport={generateReportRequestHandler.requestTracker.loading}
        consultationId={consultationId}
        consultationEndDate={consultation?.endDate}
        patientDob={consultation?.patient?.userInfo?.birthday}
        isGeneratingFSP={generateFspRequestHandler.requestTracker.loading}
        isLocal={isLocal}
        initialFSPValues={initialFSPValues}
        initialReportValues={initialReportValues}
        onGenerateFsp={handleGenerateFSP}
        onGenerateReport={handleGenerateReport}
        onGenereatePrescriptionButtonClick={handleGeneratePrescriptionButtonClick}
        onGenerateDesmosActButtonClick={handleGenerateDesmosActButtonClick}
        onDelete={handleDelete}
        onExport={handleExport}
        onPrintImages={handlePrintImages}
        onQrClick={genereateQR}
        onRefresh={handleRefreshDocument}
        onSaveFileAnnotation={handleSaveFileAnnotation}
        onSaveFileName={handleSaveFileName}
        onSendDocumentsByEmail={handleSendDocumentsByEmail}
        onUpload={handleUpload}
        onOcrDocument={handleOcrDocument}
        isProcessingOcr={isProcessingOcr}
        processingOcrFileKey={originalArgs?.fileKey ?? null}
        qrCode={qrCode}
        readOnly={readOnly}
        formSubmissionOptions={formSubmissionOptions}
      />
      {isStaff(authUser) && authUser.externalAccounts?.ordoclic && authUser.status === 'MEDECIN' && (
        <GeneratePrescriptionDialog
          isLoading={getOrCreateOrdoclicPatientRequestHandler.requestTracker.loading}
          onClose={() => setGeneratePrescriptionDialogOpen(false)}
          onPrescriptionCreated={handleAddPrescription}
          open={generatePrescriptionDialogOpen}
          ordoclicPatientId={getExternalId(patient?.externalAccounts, 'ordoclic', authUser?.establishment?._id)}
          entityId={authUser?.establishment?.ordoclicEntityId}
        />
      )}
      {isStaff(authUser) && authUser.establishment?.desmosConfig && (
        <GenerateDesmosActDialog
          isCreating={isCreatingDesmosAct || isDesmosPatientLoading}
          onClose={() => setGenerateDesmosActDialogOpen(false)}
          onCreateAct={handleCreateDesmosAct}
          open={generateDesmosActDialogOpen}
          initialValues={initialDesmosActValues}
        />
      )}
    </>
  )
}

export default DocumentsContainer

// TODO: to be extracted to FSD
function buildFormSubmissionOptions(consultation?: Consultation) {
  if (!consultation)
    return [] as Array<{
      id: string
      submittedAt?: string
      title?: string
      fields: Array<{ fieldId: string; label: string; type: string; required: boolean; options?: string[]; value: any }>
    }>
  const formSubmissions = (consultation.formSubmissions || []) as Array<any>
  const submitted = formSubmissions.filter((s) => s.status === 'submitted')
  return submitted
    .sort((a, b) => new Date(b.submittedAt || 0).getTime() - new Date(a.submittedAt || 0).getTime())
    .map((submission) => {
      const responseById = _.keyBy(submission.responses, 'fieldId')
      return {
        id: submission._id || `${submission.submittedAt || ''}`,
        submittedAt: submission.submittedAt ? dayjs(submission.submittedAt).format('DD/MM/YYYY HH:mm') : undefined,
        title: submission.template?.title,
        fields: getFormSubmissionFields(submission),
      }
    })
}

// TODO: to be extracted to FSD
function getLastFormSubmission(consultation?: Consultation) {
  const formSubmissions = consultation?.formSubmissions?.filter((s) => s.status === 'submitted')
  return _.maxBy(formSubmissions, (s) => new Date(s.submittedAt || 0).getTime())
}

// TODO: to be extracted to FSD
function getFormSubmissionFields(formSubmission?: FormSubmission) {
  if (!formSubmission) return []

  const responseById = _.keyBy(formSubmission.responses, 'fieldId')

  return formSubmission.template.fields.map((field) => {
    let value = responseById[field.id]?.value ?? null

    if (field.type === 'boolean' && typeof value === 'boolean') {
      value = value ? 'Oui' : 'Non'
    }

    return {
      fieldId: field.id,
      label: field.label,
      type: field.type,
      required: field.required,
      options: field.options,
      value,
    }
  })
}
